<script>
  import { onMount, onDestroy } from 'svelte';
  import { csv } from 'd3-fetch';
  import { scaleLinear } from 'd3-scale';
  import { AccurateBeeswarm } from './AccurateBeeswarm.js';
  import Circle from './Basics/Circle.svelte';
  import Axis from './Basics/AxisBottomV5.svelte';
  import { Delaunay } from 'd3-delaunay';
  import Labels from './Basics/Labels.svelte';

  // Props
  let {
    data,
    voronoiColor = 'none',
    voronoiStroke = 0,
    voronoiStrokeColor = 'none'
  } = $props();

  console.log(data);
  let countriesData = data;
  // State variables

  let width = $state(600);
  let pointIndex = $state(null);
  const height = 350;
  const margin = { top: 40, right: 30, bottom: 20, left: 50 };

  // Data loading

  // Computed values
  let xDomainData = $derived(countriesData.map((d) => +d.score_Total));

  let xScale = $derived(
    scaleLinear()
      .domain([Math.min(...xDomainData), Math.max(...xDomainData)])
      .range([margin.left, width - margin.right])
  );

  let yScale = $derived(
    scaleLinear()
      .range([margin.top + height, margin.bottom])
      .domain([0, 2])
  );

  let positionedData = $derived(
    new AccurateBeeswarm(
      countriesData,
      (d) => 10,
      (d) => xScale(d.score_Total),
      1,
      0,
      0
    ).calculateYPositions()
  );

  let points = $derived(positionedData.map((d) => [d.x, d.y + height / 2]));

  let voronoi = $derived(
    points.length > 0
      ? Delaunay.from(points).voronoi([
          margin.left,
          margin.top,
          width - margin.right,
          height - margin.bottom
        ])
      : null
  );

  let found = $derived(positionedData?.[pointIndex] || null);

  function handleMouseOver(e) {
    const { offsetX, offsetY } = e;
    if (voronoi) {
      pointIndex = voronoi.delaunay.find(offsetX, offsetY);
    }
  }

  function handleMouseOut() {
    pointIndex = null;
  }

  // Cleanup
  onDestroy(() => {
    pointIndex = null;
  });
</script>

<main class="relative" bind:clientWidth={width}>
  <!-- Tooltip -->
  <div
    class="tooltip"
    role="tooltip"
    aria-live="polite"
    aria-hidden={!found}
    style="top:{found ? found.y + 175 + 'px' : 'auto'}; 
           left:{found ? found.x + 10 + 'px' : 'auto'}; 
           opacity: {found ? 1 : 0};">
    {#if found}
      {found.datum.Country}: {found.datum.score_Total}
    {/if}
  </div>

  {#if width}
    <div class="relative">
      {#if countriesData.length > 0}
        <svg
          role="presentation"
          {width}
          {height}
          class="chart"
          onmousemove={handleMouseOver}
          onmouseleave={handleMouseOut}>
          <g transform="translate(0,0)">
            <Axis {width} {height} {margin} {xScale} ticksNumber={10} />
            <Labels
              labelforx={true}
              fillColor={'#fcd34d'}
              {width}
              {height}
              {margin}
              yoffset={-300}
              xoffset={-20}
              label={'Total AI Score →'} />
          </g>

          <g transform="translate(0,{height / 2})">
            {#each positionedData as country, i}
              <Circle
                classX={country.datum.Country}
                {i}
                {yScale}
                opacity={pointIndex === i ? 0.7 : 0.3}
                stroke={pointIndex === i ? '#fcd34d' : 'red'}
                strokeWidth={pointIndex === i ? '3' : '2'}
                xValue={country.x.toFixed(1)}
                yValue={country.y.toFixed(1)}
                r={10} />
            {/each}
          </g>

          {#each positionedData as data, i}
            <path
              role="presentation"
              cursor="pointer"
              tabindex="-1"
              onmousemove={handleMouseOver}
              pointer-events="all"
              fill={voronoiColor}
              stroke={voronoiStrokeColor}
              stroke-width={voronoiStroke}
              opacity="0.1"
              d={voronoi.renderCell(i)} />
          {/each}
        </svg>
      {/if}
    </div>
  {/if}
</main>

<style>
  .tooltip {
    position: absolute;
    pointer-events: none;
    font-family: 'Poppins', sans-serif;
    min-width: 8em;
    line-height: 1.2;
    font-size: 0.875rem;
    z-index: 1;
    padding: 6px;
    color: #111827;
    background-color: #f9fafb;
    border: 1px solid #ddd;
    border-radius: 4px;
    opacity: 0;
    transition:
      left 300ms ease,
      top 300ms ease;
  }
</style>
